import {
  locateBlock, locatePage, type DraftIssue, type DraftTarget,
} from '@lib/customization/reportDesign';
import { type ReportDesignContent } from '@store/api/reportDesignEndpoints';
import { definitionOf } from './blockCatalog';

/** `t` of the localization hook. */
type Translate = (key: string, values?: Array<string | number | boolean>) => string;

/** A page by where it is: "Cover page 2". */
export const pageLabel = (content: ReportDesignContent, pageId: string, t: Translate): string => {
  const at = locatePage(content, pageId);

  if (!at) return '';

  return t(at.section === 'covers' ? 'ReportDesign_Cover_N' : 'ReportDesign_Closing_N', [at.index + 1]);
};

/** What a part of a design is called: an entry of the sections list, or the place an issue is at. */
export const targetLabel = (content: ReportDesignContent, target: DraftTarget, t: Translate): string => {
  switch (target.kind) {
    case 'details': return t('ReportDesign_Section_Details');
    case 'theme': return t('ReportDesign_Section_Theme');
    case 'header': return t('ReportDesign_Section_Header');
    case 'footer': return t('ReportDesign_Section_Footer');
    case 'summary': return t('Common_Summary');
    case 'checkpoints': return t('Common_Checkpoints');
    case 'page': return pageLabel(content, target.id, t);
    default: {
      const at = locateBlock(content, target.id);

      if (!at) return '';

      const definition = definitionOf(at.block.type);

      return `${pageLabel(content, at.page.id, t)} · ${definition ? t(definition.label) : at.block.type}`;
    }
  }
};

/** Why a draft cannot be saved as it is, in a sentence. Each is one key, never glued from parts. */
export const issueWords = (issue: DraftIssue, t: Translate): string => {
  switch (issue.kind) {
    case 'name': return t('ReportDesign_Issue_Name');
    case 'pages': return t(
      issue.section === 'covers' ? 'ReportDesign_Issue_Covers' : 'ReportDesign_Issue_Closings', [issue.limit],
    );
    case 'blocks': return t('ReportDesign_Issue_Blocks', [issue.limit]);
    case 'items': return t('ReportDesign_Issue_Items', [issue.limit]);
    case 'text': return t('ReportDesign_Issue_Text', [issue.limit]);
    case 'number': return t('ReportDesign_Issue_Number', [issue.min, issue.max]);
    case 'color': return t('ReportDesign_Issue_Color');
    case 'image': return t('ReportDesign_Issue_Image');
    default: return t('ReportDesign_Issue_Bytes', [Math.ceil(issue.bytes / 1000), Math.floor(issue.limit / 1000)]);
  }
};
